/** @jsx jsx */
import React, { type ComponentPropsWithoutRef } from 'react';
import { css, jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { N0, N40, N800, N200, Y300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import {
	type MessageDescriptorV2 as MessageDescriptor,
	useIntlV2 as useIntl,
} from '@atlassian/jira-intl';
import { INTERVAL, ROLLUP, EXPLICIT, INCOMING } from '../../../constants';
import type { Appearance, OverlayItem } from '../../../types';
import Icon from '../icon';
import UnlinkButton from '../unlink-button';
import CardDateLabel from './date-label';
import messages from './messages';

const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	padding: `${token('space.100', '8px')} ${token('space.200', '16px')}`,
	backgroundColor: token('elevation.surface.raised', N0),
	boxShadow: token('elevation.shadow.raised', `0 1px ${N40}, 1px 3px 3px 0 rgba(9, 30, 66, 0.13)`),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:nth-last-child()': {
		borderBottom: 'none',
	},
});

const contentStyles = css({
	display: 'flex',
	flexDirection: 'row',
});

const contentLeftStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	overflow: 'hidden',
});

const titleContainerStyles = css({
	display: 'flex',
	position: 'relative',
	flexDirection: 'row',
	alignItems: 'center',
	overflow: 'hidden',
});

const typeIconContainerStyles = css({
	display: 'flex',
	marginRight: token('space.100', '8px'),
});

const unlinkIconWrapperStyles = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	width: token('space.200', '16px'),
	height: token('space.300', '24px'),
});

const titleStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '14px',
	lineHeight: '24px',
	color: token('color.text', N800),
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

const warningIconWrapperStyles = css({
	display: 'flex',
	width: token('space.300', '24px'),
});

const warningMessageStyles = css({
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '11px',
	fontWeight: 600,
});

const childContainerStyles = css({
	position: 'relative',
	paddingLeft: token('space.300', '24px'),
	marginTop: token('space.050', '4px'),
});

const parentTitleStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '12px',
	lineHeight: '24px',
	color: token('color.text.subtlest', N200),
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
});

type Props = {
	isOverlapping?: boolean;
} & OverlayItem & {
		appearance: Appearance;
		onUnlink?: (analyticsEvent: UIAnalyticsEvent) => void; // optional handler, controls visibility of the unlink button
	};

const renderWarningMessage = (key: string, message: string) => (
	<div css={warningMessageStyles} key={key}>
		<div css={warningIconWrapperStyles}>
			<WarningIcon primaryColor={token('color.icon.warning', Y300)} size="small" label={message} />
		</div>
		{message}
	</div>
);

const DependenciesCardContent = ({
	appearance,
	isOverlapping = false,
	startDateType,
	dueDateType,
	title,
	iconUrl,
	typeName,
	startDate,
	dueDate,
	onUnlink,
	intervalName,
	warningMessages,
}: Flow.Diff<
	// @ts-expect-error - TS2344 - Type 'Props' does not satisfy the constraint '{ parent: { title: string; iconUrl: string; } | undefined; }'.
	Props,
	{
		parent: Props['parent'];
	}
>) => {
	const { formatMessage } = useIntl();

	const getCardDateLabelProps = (): JSX.LibraryManagedAttributes<
		typeof CardDateLabel,
		ComponentPropsWithoutRef<typeof CardDateLabel>
	> => {
		let labelProps: { caption: string; noDateMessage: string } = {
			caption: '',
			noDateMessage: '',
		};
		const isIncoming = appearance === INCOMING;
		const dateType = isIncoming ? startDateType : dueDateType;

		switch (dateType) {
			case INTERVAL:
				labelProps = {
					caption: formatMessage(
						isIncoming ? messages.sprintStartDateCaption : messages.sprintEndDateCaption,
					),
					noDateMessage: formatMessage(messages.noSprintAssigned),
				};
				break;

			case ROLLUP:
			case EXPLICIT:
			default:
				labelProps = {
					caption: formatMessage(isIncoming ? messages.startDateCaption : messages.dueDateCaption),
					noDateMessage: formatMessage(
						isIncoming ? messages.noStartDateSet : messages.noDueDateSet,
					),
				};
				break;
		}

		return {
			dateType,
			date: isIncoming ? startDate : dueDate,
			intervalName,
			...labelProps,
		};
	};

	return (
		<>
			<div css={contentStyles}>
				<div css={contentLeftStyles}>
					<div css={titleContainerStyles}>
						<div css={typeIconContainerStyles}>
							<Icon src={iconUrl || ''} alt={formatMessage(messages.iconAltText, { typeName })} />
						</div>
						<span css={titleStyles} title={title}>
							{title}
						</span>
					</div>
					<CardDateLabel {...getCardDateLabelProps()} />
				</div>
				{onUnlink && (
					<div css={unlinkIconWrapperStyles}>
						<UnlinkButton onClick={onUnlink} />
					</div>
				)}
			</div>

			{isOverlapping &&
				renderWarningMessage(
					messages.datesOverlapWarning.id,
					formatMessage(messages.datesOverlapWarning),
				)}
			{warningMessages.map((message: MessageDescriptor) =>
				renderWarningMessage(message.id, formatMessage(message)),
			)}
		</>
	);
};

const DependencyCard = ({ parent, ...props }: Props) => {
	const dependenciesCardContent = <DependenciesCardContent {...props} />;

	return (
		<div css={containerStyles}>
			{parent !== undefined ? (
				<>
					<div css={titleContainerStyles}>
						<div css={typeIconContainerStyles}>
							<Icon src={parent.iconUrl} />
						</div>
						<span css={parentTitleStyles} title={parent.title}>
							{parent.title} /
						</span>
					</div>
					<div css={childContainerStyles}>{dependenciesCardContent}</div>
				</>
			) : (
				dependenciesCardContent
			)}
		</div>
	);
};

DependencyCard.displayName = 'DependencyCard';

export default DependencyCard;
